import { SelectChangeEvent } from '@mui/material'
import { useState, useEffect, useMemo } from 'react'

import {
  TFilterOption,
  TProcedureFiltersModalLogicResult,
} from './ProcedureFilters.types'
import { Surgery } from 'common/types'
import { TProcedureFilters } from 'lib/hooks/useFilters'
import { TPatientStatusOption } from 'lib/services/restApi/organizations/organizations.types'
import { Option } from 'common/types'

export const useProcedureFiltersModalLogic = (
  open: boolean,
  filters: TProcedureFilters,
  surgeries: Surgery[] = [],
  onApplyFilters: (filters: TProcedureFilters) => void,
  onClearFilters: () => void,
  onClose: () => void
): TProcedureFiltersModalLogicResult => {
  const [localFilters, setLocalFilters] = useState<TProcedureFilters>(filters)

  const patientStatusOptions: (Option & { index: number })[] = useMemo(() => {
    const options = JSON.parse(
      localStorage.getItem('patientStatusOptions') || '[]'
    )
    if (!options || options.length === 0) {
      return []
    }

    const mappedOptions = options.map((option: TPatientStatusOption) => ({
      id: option.status,
      name: option.value,
      index: option.id,
    }))

    const hasScheduled = mappedOptions.some(
      (option: Option & { index: number }) => option.id === 'SCHEDULED'
    )
    if (!hasScheduled) {
      mappedOptions.unshift({
        id: 'SCHEDULED',
        name: 'Scheduled',
        index: 0,
      })
    }

    return mappedOptions
  }, [])

  useEffect(() => {
    setLocalFilters(filters)
  }, [filters, open])

  const locationOptions: TFilterOption[] = useMemo(() => {
    const locationSet = new Set<string>()

    surgeries.forEach((surgery) => {
      if (surgery.visit?.location?.department) {
        locationSet.add(surgery.visit.location.department)
      }
    })

    localFilters.locations.forEach((location) => {
      locationSet.add(location)
    })

    return Array.from(locationSet)
      .map((location) => ({ value: location, label: location }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [surgeries, localFilters.locations])

  const roomOptions: TFilterOption[] = useMemo(() => {
    const roomSet = new Set<string>()

    surgeries.forEach((surgery) => {
      if (surgery.visit?.location?.room) {
        roomSet.add(surgery.visit.location.room)
      }
    })

    localFilters.rooms.forEach((room) => {
      roomSet.add(room)
    })

    return Array.from(roomSet)
      .map((room) => ({ value: room, label: `Room ${room}` }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [surgeries, localFilters.rooms])

  const surgeonOptions: TFilterOption[] = useMemo(() => {
    const surgeonSet = new Set<string>()

    surgeries.forEach((surgery) => {
      if (surgery.visit?.attendingProvider) {
        const provider = surgery.visit.attendingProvider
        const fullName = `${provider.firstName || ''} ${
          provider.lastName || ''
        }`.trim()
        if (fullName) surgeonSet.add(fullName)
      }
    })

    if (localFilters.surgeonName) {
      surgeonSet.add(localFilters.surgeonName)
    }

    return Array.from(surgeonSet)
      .map((surgeon) => ({ value: surgeon, label: surgeon }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [surgeries, localFilters.surgeonName])

  const surgeryNameOptions: TFilterOption[] = useMemo(() => {
    const surgeryNameSet = new Set<string>()

    surgeries.forEach((surgery) => {
      if (surgery.procedures && surgery.procedures.length > 0) {
        surgery.procedures.forEach((procedure) => {
          if (procedure.description) {
            surgeryNameSet.add(procedure.description)
          }
        })
      }
    })

    if (localFilters.surgeryName) {
      surgeryNameSet.add(localFilters.surgeryName)
    }

    return Array.from(surgeryNameSet)
      .map((name) => ({ value: name, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [surgeries, localFilters.surgeryName])

  const handleArrayFilterChange = (
    event: SelectChangeEvent<string[]>,
    filterKey: 'locations' | 'rooms'
  ) => {
    const {
      target: { value },
    } = event

    const selectedValues = typeof value === 'string' ? value.split(',') : value

    setLocalFilters((prev) => ({
      ...prev,
      [filterKey]: selectedValues,
    }))
  }

  const handleApply = () => {
    onApplyFilters(localFilters)
    onClose()
  }

  const handleClear = () => {
    onClearFilters()
    onClose()
  }

  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: 225,
        width: 250,
      },
    },
  }

  return {
    MenuProps,
    roomOptions,
    localFilters,
    surgeonOptions,
    locationOptions,
    surgeryNameOptions,
    handleApply,
    handleClear,
    setLocalFilters,
    handleArrayFilterChange,
    patientStatusOptions,
  }
}
